import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LOGO_URL_TIGHT } from "@/lib/brand";

// ─────────────────────────────────────────────────────────────────
// Prototype B — "The Ambient Arrival"
//
// Not navigation. Presence.
//
// Design intent (from the user's brief):
//   • The homepage should feel like arriving at a CrewZNatioN meet.
//   • Belonging before understanding.
//   • The photograph is the interface itself, not content.
//   • Emotional recognition over website conventions.
//   • Every decision must answer: does this feel like arriving?
//
// Composition:
//   • A single full-bleed community photograph serves as the
//     ambient environment — the atmosphere of arrival. It breathes
//     via a slow ken-burns drift so the space feels alive, not
//     photographed.
//   • Four "moments" fade in on stagger — like memories entering
//     peripheral vision after the eyes adjust. Each moment carries
//     an emotional category (flagship, movement, memory,
//     invitation), not a page title.
//   • No navbar. No CTAs. No arrows. No icons. No chips.
//   • The wordmark is present but quiet. The language toggle is
//     present but quiet. Nothing shouts.
//   • Emotional labels are whisper-typography, revealed by
//     approaching a moment — recognition, not instruction.
//
// Preserves the approved English and Arabic homepages entirely.
// Not part of the bilingual site. Lives only as a prototype.
// ─────────────────────────────────────────────────────────────────

// Base atmosphere — the community itself. This photograph carries
// the emotional category BELONGING implicitly; no floating card is
// needed for it.
const AMBIENT_BASE = {
    image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/u65gd357_IMG-20260503-WA0051.jpg",
    // "Inside the meet — Riyadh comes together"
};

// Floating moments — each carries an emotional category, positioned
// organically on the canvas rather than on a grid. Rotations,
// positions, and reveal timings are hand-tuned to feel scattered
// yet composed (as memories are).
const AMBIENT_MOMENTS = [
    {
        id: "flagship",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/tf8y44s5_F3209F41-0B30-4C13-8807-7FCBDFB18ABD.PNG",
        // "Not Tokyo — This is Riyadh"
        emotionEn: "Flagship",
        emotionAr: "الشعار",
        to: "/gathering",
        // Position (percent), rotation (deg), reveal delay (ms)
        top: "12%",
        right: "6%",
        rotate: -2.2,
        delay: 1400,
        width: 260,
    },
    {
        id: "movement",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/l3h9h8v0_35B1CA2D-6A44-433F-A0BB-19EE971F256E.JPEG",
        // "Corvette trio under the festival lights"
        emotionEn: "Movement",
        emotionAr: "الحركة",
        to: "/events",
        top: "42%",
        left: "4%",
        rotate: 1.8,
        delay: 2400,
        width: 240,
    },
    {
        id: "memory",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/eyed6v8w_E4BE464A-17CA-400C-9126-D68CE352C4E2.JPG",
        // "1958 in full bloom — purple lowrider with roses"
        emotionEn: "Memory",
        emotionAr: "الذاكرة",
        to: "/gallery",
        bottom: "10%",
        left: "26%",
        rotate: -1.6,
        delay: 3400,
        width: 230,
    },
    {
        id: "invitation",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/bf40bx41_IMG_6392.jpg",
        // "Cadillac on three — Riyadh lowrider showcase" — a character
        // moment that reads as invitation-to-look-closer.
        emotionEn: "Invitation",
        emotionAr: "حياك",
        to: "/contact",
        bottom: "16%",
        right: "10%",
        rotate: 2.6,
        delay: 4400,
        width: 250,
    },
];

// One moment — a small authentic photograph placed on the ambient
// canvas. Enters via a slow fade + slight rise + slight scale. On
// approach (hover), it settles (rotation snaps toward 0) and its
// emotional label brightens — as if picked up to be considered.
function AmbientMoment({ moment }) {
    const positionStyle = {
        top: moment.top,
        bottom: moment.bottom,
        left: moment.left,
        right: moment.right,
        width: `${moment.width}px`,
        // The animation itself is CSS-only for smoothness. We hand off
        // the rotation via CSS variable so the hover state can
        // interpolate cleanly toward 0deg.
        "--rotate": `${moment.rotate}deg`,
        "--delay": `${moment.delay}ms`,
    };

    return (
        <Link
            to={moment.to}
            className="ambient-moment group absolute block"
            style={positionStyle}
            data-testid={`ambient-moment-${moment.id}`}
            aria-label={moment.emotionEn}
        >
            <div className="relative aspect-square w-full overflow-hidden ring-1 ring-white/5 shadow-[0_28px_60px_-12px_rgba(0,0,0,0.7),0_10px_20px_-6px_rgba(0,0,0,0.5)] transition-transform duration-700 ease-out group-hover:scale-[1.035]">
                <img
                    src={moment.image}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]"
                    draggable={false}
                />
                {/* Whisper of contrast beneath the label — no opaque bar */}
                <div
                    className="absolute inset-x-0 bottom-0 h-16 pointer-events-none"
                    style={{
                        background:
                            "linear-gradient(to top, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.28) 55%, transparent 100%)",
                    }}
                    aria-hidden="true"
                />
                <div className="absolute inset-x-3 bottom-3 flex items-baseline gap-3 justify-between">
                    <span
                        className="font-body uppercase tracking-[0.22em] text-[10px] text-white/70 group-hover:text-white transition-colors duration-500"
                        dir="ltr"
                    >
                        {moment.emotionEn}
                    </span>
                    <span
                        dir="rtl"
                        className="font-arabic text-[13px] font-bold text-white/60 group-hover:text-white transition-colors duration-500 leading-none"
                    >
                        {moment.emotionAr}
                    </span>
                </div>
            </div>
        </Link>
    );
}

export default function AmbientPrototype() {
    const location = useLocation();
    const isAr = location.search.includes("lang=ar");
    const toggleTo = isAr ? "/prototypes/ambient" : "/prototypes/ambient?lang=ar";

    return (
        <div
            className="ambient-root min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)] overflow-hidden"
            data-testid="ambient-prototype"
            dir={isAr ? "rtl" : "ltr"}
        >
            <section className="relative w-screen h-screen overflow-hidden">
                {/* AMBIENT BASE — the community photograph as environment.
                    Slow ken-burns drift so the space feels alive. This
                    photograph carries BELONGING implicitly. */}
                <div className="absolute inset-0">
                    <img
                        src={AMBIENT_BASE.image}
                        alt=""
                        aria-hidden="true"
                        className="ambient-base absolute inset-0 w-full h-full object-cover"
                        draggable={false}
                    />
                    {/* Very light atmospheric wash — preserves the
                        photograph's own colour, adds only enough depth
                        to hold the wordmark and the moments without
                        introducing a graphic overlay. */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(ellipse at 50% 55%, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.28) 55%, rgba(0,0,0,0.55) 100%)",
                        }}
                        aria-hidden="true"
                    />
                    {/* Quiet vignette at the top and bottom to seat the
                        wordmark and language toggle. */}
                    <div
                        className="absolute inset-x-0 top-0 h-40 pointer-events-none"
                        style={{
                            background:
                                "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)",
                        }}
                        aria-hidden="true"
                    />
                    <div
                        className="absolute inset-x-0 bottom-0 h-56 pointer-events-none"
                        style={{
                            background:
                                "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.2) 55%, transparent 100%)",
                        }}
                        aria-hidden="true"
                    />
                </div>

                {/* Quiet header — wordmark and language toggle only. No
                    navigation. The photograph is the navigation. */}
                <div
                    className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-6 md:px-10 lg:px-16 h-20"
                    data-testid="ambient-header"
                >
                    <Link to="/" className="inline-flex items-center" data-testid="ambient-wordmark">
                        <img
                            src={LOGO_URL_TIGHT}
                            alt="CrewZNatioN"
                            className="h-9 md:h-11 w-auto select-none opacity-85 hover:opacity-100 transition-opacity duration-500"
                            draggable={false}
                        />
                    </Link>
                    <Link
                        to={toggleTo}
                        className={
                            isAr
                                ? "font-body uppercase tracking-[0.22em] text-[11px] font-bold text-white/70 hover:text-white transition-colors duration-500"
                                : "font-arabic normal-case tracking-normal text-[13px] font-bold text-white/70 hover:text-white transition-colors duration-500"
                        }
                        dir={isAr ? "ltr" : "rtl"}
                        data-testid="ambient-lang-switcher"
                    >
                        {isAr ? "English" : "العربية"}
                    </Link>
                </div>

                {/* FLOATING MOMENTS — scattered, staggered, unhurried */}
                <div className="absolute inset-0 z-10 pointer-events-none">
                    {AMBIENT_MOMENTS.map((m) => (
                        <div key={m.id} className="pointer-events-auto">
                            <AmbientMoment moment={m} />
                        </div>
                    ))}
                </div>
            </section>

            {/* Prototype-local CSS. Scoped to .ambient-root so it cannot
                escape and touch the approved English or Arabic sites. */}
            <style>{`
                .ambient-root .ambient-base {
                    animation: ambient-drift 40s ease-in-out infinite alternate;
                    transform-origin: center 60%;
                }
                @keyframes ambient-drift {
                    0%   { transform: scale(1.06) translate3d(-0.5%, -0.3%, 0); }
                    100% { transform: scale(1.12) translate3d(0.6%,  0.4%, 0); }
                }
                .ambient-root .ambient-moment {
                    opacity: 0;
                    transform: translateY(14px) rotate(var(--rotate));
                    animation: ambient-moment-in 1600ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
                    animation-delay: var(--delay);
                    will-change: transform, opacity;
                    transition: transform 700ms cubic-bezier(0.22, 0.61, 0.36, 1);
                }
                @keyframes ambient-moment-in {
                    0%   { opacity: 0; transform: translateY(14px) rotate(var(--rotate)) scale(0.985); }
                    100% { opacity: 1; transform: translateY(0)    rotate(var(--rotate)) scale(1); }
                }
                .ambient-root .ambient-moment:hover {
                    /* Rotation settles toward 0 — as if the moment is
                       being picked up to be considered. */
                    transform: translateY(-3px) rotate(calc(var(--rotate) * 0.15));
                }
                @media (prefers-reduced-motion: reduce) {
                    .ambient-root .ambient-base { animation: none; }
                    .ambient-root .ambient-moment {
                        animation: none;
                        opacity: 1;
                        transform: rotate(var(--rotate));
                    }
                }
            `}</style>
        </div>
    );
}
